import { GymSchema, MemberScheduleSchema, OccupancyDigestSchema, } from "./schemas.js";
import { canonicalJson } from "./utils.js";
/**
 * Ordine totale, non solo cronologico. Il tempo decide quasi sempre; a parita' di
 * istante decide l'autore; a parita' di autore — lo stesso utente su due
 * dispositivi, con orologi allineati al millisecondo — decide il contenuto.
 * Senza l'ultimo gradino il merge non sarebbe una funzione, e due repliche
 * potrebbero fermarsi su valori diversi credendo di aver finito.
 */
function compare(left, leftRevision, right, rightRevision) {
    const byTime = Date.parse(leftRevision.at) - Date.parse(rightRevision.at);
    if (byTime !== 0)
        return byTime;
    const byAuthor = leftRevision.by.localeCompare(rightRevision.by);
    if (byAuthor !== 0)
        return byAuthor;
    return canonicalJson(left).localeCompare(canonicalJson(right));
}
/**
 * Un orologio che non torna indietro. Due modifiche nello stesso millisecondo —
 * o un orologio di sistema che arretra — renderebbero la scelta arbitraria: chi
 * scrive dopo, sulla stessa cosa, deve avere una revisione maggiore di quella
 * che sostituisce, non uguale.
 */
export function advance(now, previous) {
    const nowMs = Date.parse(now);
    const previousMs = previous === undefined ? Number.NaN : Date.parse(previous);
    if (!Number.isFinite(previousMs) || nowMs > previousMs)
        return new Date(nowMs).toISOString();
    return new Date(previousMs + 1).toISOString();
}
function mergeKeyed(entries, key, revision) {
    const winners = new Map();
    for (const entry of entries) {
        const previous = winners.get(key(entry));
        if (!previous || compare(entry, revision(entry), previous, revision(previous)) > 0) {
            winners.set(key(entry), entry);
        }
    }
    return [...winners.values()].sort((left, right) => key(left).localeCompare(key(right)));
}
function isNewer(candidate, candidateRevision, other, otherRevision) {
    return compare(candidate, candidateRevision, other, otherRevision) > 0;
}
// --- Palestra -----------------------------------------------------------------
function slotRevision(slot, fallback) {
    return { at: slot.updatedAt ?? fallback.at, by: slot.updatedBy ?? fallback.by };
}
function retiredRevision(entry) {
    return { at: entry.updatedAt, by: entry.updatedBy };
}
function exceptionRevision(entry) {
    return { at: entry.updatedAt, by: entry.authorId };
}
/**
 * Forma canonica: gli slot portano la propria revisione anche quando il
 * documento e' nato prima di questa regola, l'ordine e' quello delle chiavi e non
 * quello di arrivo, e gli slot uccisi da una lapide piu' recente non compaiono
 * fra quelli vivi. Due documenti che dicono la stessa cosa si normalizzano nello
 * stesso testo: e' cio' che rende confrontabili gli esiti di un merge.
 */
export function normalizeGym(gym) {
    const header = { at: gym.updatedAt, by: gym.updatedBy };
    const stamped = gym.slots.map((slot) => ({
        ...slot,
        updatedAt: slot.updatedAt ?? header.at,
        updatedBy: slot.updatedBy ?? header.by,
    }));
    const retiredSlots = mergeKeyed(gym.retiredSlots, (entry) => entry.id, retiredRevision);
    const tombstones = new Map(retiredSlots.map((entry) => [entry.id, entry]));
    const slots = mergeKeyed(stamped, (slot) => slot.id, (slot) => slotRevision(slot, header)).filter((slot) => {
        const tombstone = tombstones.get(slot.id);
        return (!tombstone || isNewer(slot, slotRevision(slot, header), tombstone, retiredRevision(tombstone)));
    });
    return GymSchema.parse({
        schemaVersion: gym.schemaVersion,
        id: gym.id,
        name: gym.name,
        timeZone: gym.timeZone,
        slotDurationMinutes: gym.slotDurationMinutes,
        slots,
        retiredSlots,
        exceptions: mergeKeyed(gym.exceptions, (entry) => entry.date, exceptionRevision),
        updatedAt: gym.updatedAt,
        updatedBy: gym.updatedBy,
    });
}
/**
 * Nome, fuso orario e durata degli slot cambiano insieme e raramente: li decide
 * la revisione di testata, in blocco. Slot, lapidi ed eccezioni no: quelli si uniscono chiave per
 * chiave, altrimenti aggiungere un corso il lunedi' dal telefono cancellerebbe
 * quello aggiunto il martedi' dal computer.
 */
export function mergeGym(left, right) {
    if (left.id !== right.id)
        throw new Error("Merge fra palestre diverse rifiutato");
    const [first, second] = [normalizeGym(left), normalizeGym(right)];
    const firstHeader = { at: first.updatedAt, by: first.updatedBy };
    const secondHeader = { at: second.updatedAt, by: second.updatedBy };
    const headerFrom = isNewer({ name: first.name, timeZone: first.timeZone, slotDurationMinutes: first.slotDurationMinutes }, firstHeader, {
        name: second.name,
        timeZone: second.timeZone,
        slotDurationMinutes: second.slotDurationMinutes,
    }, secondHeader)
        ? first
        : second;
    return normalizeGym(GymSchema.parse({
        schemaVersion: first.schemaVersion,
        id: first.id,
        name: headerFrom.name,
        timeZone: headerFrom.timeZone,
        slotDurationMinutes: headerFrom.slotDurationMinutes,
        updatedAt: headerFrom.updatedAt,
        updatedBy: headerFrom.updatedBy,
        slots: [...first.slots, ...second.slots],
        // Le lapidi dei due lati vanno unite prima di applicarle: un merge che
        // guardasse solo gli slot vivi resusciterebbe cio' che l'altro ha rimosso.
        retiredSlots: [...first.retiredSlots, ...second.retiredSlots],
        exceptions: [...first.exceptions, ...second.exceptions],
    }));
}
// --- Pianificazione personale -------------------------------------------------
/** La chiave dell'occorrenza: una sola decisione per data e slot di partenza. */
export function scheduleExceptionKey(entry) {
    return `${entry.date}:${entry.kind === "added" ? entry.slotId : entry.sourceSlotId}`;
}
function scheduleExceptionRevision(entry, fallback) {
    return { at: entry.updatedAt ?? fallback.at, by: entry.updatedBy ?? fallback.by };
}
function recurringRevision(entry) {
    return { at: entry.updatedAt, by: entry.updatedBy };
}
/**
 * `recurringSlotIds` e' una lettura, `recurring` e' la scrittura. Un documento
 * arrivato dalle prime versioni ha solo la lettura: in quel caso — e solo in
 * quello — le ricorrenze si datano con la testata del documento. Dopo, l'elenco
 * derivato non ha piu' voce in capitolo, altrimenti una prenotazione fatta su un
 * dispositivo rimasto indietro farebbe tornare gli orari che l'altro ha tolto.
 */
export function normalizeMemberSchedule(schedule) {
    const header = { at: schedule.updatedAt, by: schedule.memberId };
    const seeded = schedule.recurring.length === 0
        ? schedule.recurringSlotIds.map((slotId) => ({
            slotId,
            active: true,
            updatedAt: header.at,
            updatedBy: header.by,
        }))
        : [...schedule.recurring];
    const recurring = mergeKeyed(seeded, (entry) => entry.slotId, recurringRevision);
    const exceptions = mergeKeyed(schedule.exceptions.map((entry) => ({
        ...entry,
        updatedAt: entry.updatedAt ?? header.at,
        updatedBy: entry.updatedBy ?? header.by,
    })), scheduleExceptionKey, (entry) => scheduleExceptionRevision(entry, header));
    const conflicts = [
        ...new Map([...schedule.conflicts]
            .sort((left, right) => Number(Boolean(left.resolvedTo)) - Number(Boolean(right.resolvedTo)))
            .map((entry) => [entry.occurrenceKey, entry])).values(),
    ].sort((left, right) => left.occurrenceKey.localeCompare(right.occurrenceKey));
    return MemberScheduleSchema.parse({
        schemaVersion: schedule.schemaVersion,
        gymId: schedule.gymId,
        memberId: schedule.memberId,
        displayName: schedule.displayName,
        recurring,
        recurringSlotIds: recurring.filter((entry) => entry.active).map((entry) => entry.slotId),
        exceptions,
        conflicts,
        updatedAt: schedule.updatedAt,
    });
}
export function mergeMemberSchedule(left, right) {
    if (left.gymId !== right.gymId || left.memberId !== right.memberId) {
        throw new Error("Merge fra pianificazioni di persone diverse rifiutato");
    }
    const [first, second] = [normalizeMemberSchedule(left), normalizeMemberSchedule(right)];
    const newer = isNewer(first.displayName, { at: first.updatedAt, by: first.memberId }, second.displayName, { at: second.updatedAt, by: second.memberId })
        ? first
        : second;
    return normalizeMemberSchedule(MemberScheduleSchema.parse({
        schemaVersion: first.schemaVersion,
        gymId: first.gymId,
        memberId: first.memberId,
        displayName: newer.displayName,
        updatedAt: newer.updatedAt,
        recurring: [...first.recurring, ...second.recurring],
        recurringSlotIds: [],
        exceptions: [...first.exceptions, ...second.exceptions],
        conflicts: [...first.conflicts, ...second.conflicts],
    }));
}
// --- Capienza -----------------------------------------------------------------
function occupancyRevision(entry) {
    return { at: entry.updatedAt, by: entry.updatedBy };
}
export function normalizeOccupancyDigest(digest) {
    return OccupancyDigestSchema.parse({
        ...digest,
        entries: mergeKeyed(digest.entries, (entry) => entry.occurrenceKey, occupancyRevision),
    });
}
/**
 * Due coach possono pubblicare la capienza nello stesso momento partendo da viste
 * diverse: l'unione avviene per singola occorrenza, cosi' il conteggio di lunedi'
 * visto da uno non cancella quello di martedi' visto dall'altro.
 */
export function mergeOccupancyDigest(left, right) {
    if (left.gymId !== right.gymId)
        throw new Error("Merge fra palestre diverse rifiutato");
    const newer = isNewer(left.entries.length, { at: left.generatedAt, by: left.generatedBy }, right.entries.length, { at: right.generatedAt, by: right.generatedBy })
        ? left
        : right;
    return normalizeOccupancyDigest(OccupancyDigestSchema.parse({
        schemaVersion: left.schemaVersion,
        gymId: left.gymId,
        generatedAt: newer.generatedAt,
        generatedBy: newer.generatedBy,
        entries: [...left.entries, ...right.entries],
    }));
}
/** Le giornate passate non interessano nessuno e farebbero crescere il digest. */
export function pruneOccupancyDigest(digest, fromDate) {
    return OccupancyDigestSchema.parse({
        ...digest,
        entries: digest.entries.filter((entry) => entry.date >= fromDate),
    });
}
/** Uguaglianza fra documenti normalizzati: dice se un merge ha aggiunto qualcosa. */
export function sameDocument(left, right) {
    return canonicalJson(left) === canonicalJson(right);
}
